/**
 * Unified analytics tracking utility
 * Sends events to GA4, Algolia Analytics, and Sentry
 */

import * as Sentry from '@sentry/react';
import type { AnalyticsEventParams, AlgoliaAnalyticsEvent, TrackingResult, AnalyticsEventAction, AnalyticsEventCategory } from '../types/analytics';
import type { AnalyticsEvent } from '../types/analytics-events';
import { sendGA4Event } from './providers/ga4';

// Declare window.dataLayer for GA4
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    aa?: (...args: any[]) => void;
  }
}

/**
 * Convert AnalyticsEvent to AnalyticsEventParams format for backward compatibility
 */
function convertTypedEventToParams(event: AnalyticsEvent): AnalyticsEventParams {
  const baseParams: AnalyticsEventParams = {
    category: 'interaction' as AnalyticsEventCategory,
    action: event.name as AnalyticsEventAction,
  };

  switch (event.name) {
    case 'job_view':
      baseParams.category = 'job';
      baseParams.job_id = event.params.job_id;
      baseParams.job_title = event.params.job_title;
      baseParams.company_id = event.params.company_id;
      baseParams.label = event.params.job_title;
      if (event.params.company_name) baseParams.company_name = event.params.company_name;
      if (event.params.location_label) baseParams.location_label = event.params.location_label;
      if (event.params.is_remote !== undefined) baseParams.is_remote = event.params.is_remote;
      if (event.params.has_salary_insight !== undefined) baseParams.has_salary_insight = event.params.has_salary_insight;
      break;
      
    case 'company_view':
      baseParams.category = 'company' as any;
      baseParams.company_id = event.params.company_id;
      baseParams.label = event.params.company_name || event.params.company_id;
      if (event.params.company_name) baseParams.company_name = event.params.company_name;
      if (event.params.industry) baseParams.industry = event.params.industry;
      break;
      
    case 'srp_view':
      baseParams.category = 'search';
      baseParams.query = event.params.query_text;
      baseParams.hits_count = event.params.result_count;
      baseParams.label = event.params.query_text || 'srp_view';
      if (event.params.location_label) baseParams.location_label = event.params.location_label;
      if (event.params.filters_applied) baseParams.filters_applied = event.params.filters_applied;
      break;
      
    case 'job_apply_click':
      baseParams.category = 'job';
      baseParams.job_id = event.params.job_id;
      baseParams.company_id = event.params.company_id;
      baseParams.label = 'job_apply';
      if (event.params.apply_destination) baseParams.apply_destination = event.params.apply_destination;
      break;
      
    case 'salary_insight_view':
      baseParams.category = 'job';
      baseParams.job_id = event.params.job_id;
      baseParams.company_id = event.params.company_id;
      baseParams.label = 'salary_insight_view';
      if (event.params.has_exact_salary !== undefined) baseParams.has_exact_salary = event.params.has_exact_salary;
      if (event.params.has_estimated_salary !== undefined) baseParams.has_estimated_salary = event.params.has_estimated_salary;
      break;
      
    case 'salary_insight_interaction':
      baseParams.category = 'job';
      baseParams.job_id = event.params.job_id;
      baseParams.company_id = event.params.company_id;
      baseParams.label = event.params.interaction_type;
      baseParams.interaction_type = event.params.interaction_type;
      break;
      
    case 'job_click':
      baseParams.category = 'job';
      baseParams.job_id = event.params.job_id;
      baseParams.job_title = event.params.job_title;
      baseParams.company_id = event.params.company_id;
      baseParams.label = event.params.job_title;
      if (event.params.position !== undefined) baseParams.value = event.params.position;
      break;
      
    case 'job_save':
    case 'job_unsave':
    case 'job_share':
      baseParams.category = 'job';
      baseParams.job_id = event.params.job_id;
      baseParams.job_title = event.params.job_title;
      baseParams.label = event.params.job_title;
      break;
      
    case 'filter_add':
    case 'filter_remove':
      baseParams.category = 'search';
      baseParams.filter_key = event.params.filter_key;
      baseParams.filter_value = event.params.filter_value;
      baseParams.label = `${event.params.filter_key}: ${event.params.filter_value}`;
      break;
      
    case 'sort_change':
      baseParams.category = 'search';
      baseParams.sort_type = event.params.sort_type;
      baseParams.label = event.params.sort_type;
      break;
      
    case 'search_query':
      baseParams.category = 'search';
      baseParams.query = event.params.query;
      baseParams.hits_count = event.params.hits_count;
      baseParams.label = event.params.query;
      break;
      
    case 'search_pagination':
      baseParams.category = 'navigation';
      baseParams.page_number = event.params.page_number;
      baseParams.label = `Page ${event.params.page_number}`;
      break;
      
    case 'search_suggestion_click':
      baseParams.category = 'search';
      baseParams.query = event.params.suggestion;
      baseParams.label = event.params.suggestion;
      break;
      
    case 'company_link_click':
      baseParams.category = 'interaction';
      baseParams.company_id = event.params.company_id;
      baseParams.label = event.params.company_id;
      break;
      
    case 'page_view':
      baseParams.category = 'navigation';
      baseParams.label = event.params.page_type || 'page_view';
      if (event.params.job_id) baseParams.job_id = event.params.job_id;
      if (event.params.company_id) baseParams.company_id = event.params.company_id;
      if (event.params.srp_id) baseParams.srp_id = event.params.srp_id;
      if (event.params.page_type) baseParams.page_type = event.params.page_type;
      break;
      
    case 'error_occurred':
      baseParams.category = 'error';
      baseParams.label = event.params.error_message;
      if (event.params.error_context) baseParams.error_context = event.params.error_context;
      break;
      
    case 'web_vital':
      baseParams.category = 'performance';
      baseParams.label = event.params.metric_name;
      baseParams.value = event.params.value;
      break;
  }
  
  // Add locale if present
  if (event.params.locale) {
    baseParams.locale = event.params.locale;
  }
  
  return baseParams;
}

/**
 * Map AnalyticsEvent to Algolia event format
 */
function mapTypedEventToAlgolia(event: AnalyticsEvent): AlgoliaAnalyticsEvent {
  const algoliaEvent: AlgoliaAnalyticsEvent = {
    eventType: mapToAlgoliaEventType(event.name),
    eventName: event.name,
    timestamp: Date.now(),
  };
  
  // Add locale-based index
  if (event.params.locale) {
    algoliaEvent.index = getIndexForLocale(event.params.locale);
  }
  
  // Add object IDs for job events
  if (event.name.startsWith('job_') && 'job_id' in event.params) {
    algoliaEvent.objectIDs = [event.params.job_id];
  }
  
  // Add query ID for search events - get from sessionStorage if available
  if (event.name === 'search_query') {
    const storedQueryID = getCurrentQueryID();
    if (storedQueryID) {
      algoliaEvent.queryID = storedQueryID;
    }
  }
  
  // Add filters for filter events
  if ((event.name === 'filter_add' || event.name === 'filter_remove') && 'filter_key' in event.params) {
    algoliaEvent.filters = [`${event.params.filter_key}:${event.params.filter_value}`];
  }
  
  return algoliaEvent;
}

/**
 * Track an event across GA4, Algolia Analytics, and Sentry
 * Supports both typed AnalyticsEvent and legacy AnalyticsEventParams
 */
export function trackEvent(eventOrParams: AnalyticsEvent | AnalyticsEventParams): TrackingResult {
  const result: TrackingResult = {
    ga4: false,
    algolia: false,
    sentry: false,
  };

  // Check if this is a typed event or legacy params
  const isTypedEvent = 'name' in eventOrParams && 'params' in eventOrParams;
  
  let params: AnalyticsEventParams;
  let typedEvent: AnalyticsEvent | null = null;
  
  if (isTypedEvent) {
    typedEvent = eventOrParams as AnalyticsEvent;
    params = convertTypedEventToParams(typedEvent);
    
    // Development validation
    if (process.env.NODE_ENV === 'development') {
      // Validate required fields based on event type
      const validationErrors: string[] = [];
      
      switch (typedEvent.name) {
        case 'job_view':
          if (!typedEvent.params.job_id) validationErrors.push('job_id is required');
          if (!typedEvent.params.job_title) validationErrors.push('job_title is required');
          if (!typedEvent.params.company_id) validationErrors.push('company_id is required');
          break;
        case 'company_view':
          if (!typedEvent.params.company_id) validationErrors.push('company_id is required');
          break;
        case 'srp_view':
          if (!typedEvent.params.srp_id) validationErrors.push('srp_id is required');
          break;
        case 'job_apply_click':
          if (!typedEvent.params.job_id) validationErrors.push('job_id is required');
          if (!typedEvent.params.company_id) validationErrors.push('company_id is required');
          break;
        case 'salary_insight_view':
        case 'salary_insight_interaction':
          if (!typedEvent.params.job_id) validationErrors.push('job_id is required');
          if (!typedEvent.params.company_id) validationErrors.push('company_id is required');
          break;
      }
      
      if (validationErrors.length > 0) {
        console.warn('[Analytics] Validation errors for event:', typedEvent.name, validationErrors);
      }
      
      // Log event structure for debugging
      console.log('[Analytics] Tracking typed event:', typedEvent.name, typedEvent.params);
    }
  } else {
    params = eventOrParams as AnalyticsEventParams;
  }

  try {
    // GA4 tracking - use adapter for typed events, legacy path for old format
    if (isTypedEvent && typedEvent) {
      result.ga4 = sendGA4Event(typedEvent);
    } else {
      // Legacy GA4 tracking
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', params.action, {
          event_category: params.category,
          event_label: params.label,
          value: params.value,
          non_interaction: params.non_interaction,
          // Add custom parameters
          ...(params.query && { search_term: params.query }),
          ...(params.filter_key && { filter_key: params.filter_key }),
          ...(params.filter_value && { filter_value: params.filter_value }),
          ...(params.sort_type && { sort_type: params.sort_type }),
          ...(params.page_number && { page_number: params.page_number }),
          ...(params.hits_count && { hits_count: params.hits_count }),
          ...(params.job_id && { job_id: params.job_id }),
          ...(params.company_id && { company_id: params.company_id }),
          ...(params.job_title && { job_title: params.job_title }),
          ...(params.locale && { locale: params.locale }),
          ...(params.url && { url: params.url }),
        });
        result.ga4 = true;
      }
    }

    // Sentry tracking - add breadcrumb for all events
    if (typeof window !== 'undefined') {
      Sentry.addBreadcrumb({
        category: params.category,
        message: params.action,
        data: {
          label: params.label,
          value: params.value,
          ...params,
          ...(isTypedEvent && typedEvent ? { typed_event_name: typedEvent.name } : {}),
        },
        level: params.category === 'error' ? 'error' : 'info',
      });
      result.sentry = true;
    }

    // Algolia Analytics tracking (if configured)
    if (typeof window !== 'undefined' && window.aa) {
      try {
        const queryID = getCurrentQueryID();
        const index = params.locale ? getIndexForLocale(params.locale) : undefined;
        
        // Use proper Algolia Insights API methods
        if (isTypedEvent && typedEvent) {
          // Handle typed events
          if (typedEvent.name === 'search_query') {
            // Search view event - already handled in trackSearchQuery
            result.algolia = true;
          } else if (typedEvent.name === 'job_click' && 'job_id' in typedEvent.params) {
            // Job click event
            if (queryID) {
              window.aa('clickedObjectIDsAfterSearch', {
                eventName: 'Job Clicked',
                index: index,
                objectIDs: [typedEvent.params.job_id],
                queryID: queryID,
                positions: typedEvent.params.position ? [typedEvent.params.position] : undefined,
              });
              result.algolia = true;
            }
          } else if (typedEvent.name === 'job_view' && 'job_id' in typedEvent.params) {
            // Job view event (detail page)
            if (queryID) {
              window.aa('viewedObjectIDs', {
                eventName: 'Job Viewed',
                index: index,
                objectIDs: [typedEvent.params.job_id],
                queryID: queryID,
              });
              result.algolia = true;
            }
          } else if ((typedEvent.name === 'filter_add' || typedEvent.name === 'filter_remove') && 'filter_key' in typedEvent.params) {
            // Filter click event
            if (queryID) {
              window.aa('clickedFilters', {
                eventName: typedEvent.name === 'filter_add' ? 'Filter Added' : 'Filter Removed',
                index: index,
                filters: [`${typedEvent.params.filter_key}:${typedEvent.params.filter_value}`],
                queryID: queryID,
              });
              result.algolia = true;
            }
          }
        } else {
          // Legacy event format
          if (params.action === 'search_query') {
            // Search view - already handled in trackSearchQuery
            result.algolia = true;
          } else if (params.action === 'job_click' && params.job_id) {
            if (queryID) {
              window.aa('clickedObjectIDsAfterSearch', {
                eventName: 'Job Clicked',
                index: index,
                objectIDs: [params.job_id],
                queryID: queryID,
                positions: params.value ? [params.value] : undefined,
              });
              result.algolia = true;
            }
          } else if (params.action === 'job_view' && params.job_id) {
            if (queryID) {
              window.aa('viewedObjectIDs', {
                eventName: 'Job Viewed',
                index: index,
                objectIDs: [params.job_id],
                queryID: queryID,
              });
              result.algolia = true;
            }
          } else if ((params.action === 'filter_add' || params.action === 'filter_remove') && params.filter_key) {
            if (queryID) {
              window.aa('clickedFilters', {
                eventName: params.action === 'filter_add' ? 'Filter Added' : 'Filter Removed',
                index: index,
                filters: [`${params.filter_key}:${params.filter_value}`],
                queryID: queryID,
              });
              result.algolia = true;
            }
          }
        }
      } catch (algoliaError) {
        console.warn('[Analytics] Failed to send Algolia event:', algoliaError);
      }
    }
  } catch (error) {
    console.error('[Analytics] Error tracking event:', error);
    // Report error to Sentry
    Sentry.captureException(error, {
      tags: { component: 'analytics' },
      extra: { params, isTypedEvent, typedEventName: isTypedEvent && typedEvent ? typedEvent.name : undefined },
    });
  }

  return result;
}

/**
 * Map our event actions to Algolia event types
 */
function mapToAlgoliaEventType(action: string): string {
  const mapping: Record<string, string> = {
    'search_query': 'view',
    'job_click': 'click',
    'job_view': 'view',
    'filter_add': 'click',
    'filter_remove': 'click',
    'sort_change': 'click',
  };
  return mapping[action] || 'view';
}

/**
 * Get Algolia index name for locale
 */
function getIndexForLocale(locale: string): string {
  const mapping: Record<string, string> = {
    'fi': 'fi_hae',
    'sv': 'sv_sok',
    'en': 'en_search',
  };
  return mapping[locale] || 'en_search';
}

/**
 * Generate a unique query ID for tracking search session
 */
function generateQueryID(): string {
  return `query_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Store query ID in session storage for tracking across user actions
 */
function storeQueryID(queryID: string, query: string, hitsCount?: number) {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('current_query_id', queryID);
    sessionStorage.setItem(`query_${queryID}`, JSON.stringify({
      query,
      hitsCount,
      timestamp: Date.now(),
      url: window.location.href,
    }));
  }
}

/**
 * Get the current query ID from session storage
 */
function getCurrentQueryID(): string | null {
  if (typeof window !== 'undefined') {
    return sessionStorage.getItem('current_query_id');
  }
  return null;
}

/**
 * Clear the current query ID (called on new search)
 * Export for use in SearchContext
 */
export function clearQueryID() {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem('current_query_id');
  }
}

/**
 * Track a search query
 */
export function trackSearchQuery(params: {
  query: string;
  locale?: string;
  hitsCount?: number;
  queryID?: string; // Algolia queryID from search results
}) {
  // Use Algolia's queryID if provided, otherwise generate one
  const queryID = params.queryID || generateQueryID();
  storeQueryID(queryID, params.query, params.hitsCount);
  
  // Send Algolia view event with proper queryID
  if (typeof window !== 'undefined' && window.aa) {
    const index = params.locale ? getIndexForLocale(params.locale) : undefined;
    
    try {
      // Use viewedObjectIDs for search view events (Algolia Insights API)
      window.aa('viewedObjectIDs', {
        eventName: 'Search',
        index: index,
        objectIDs: [], // Search view doesn't need objectIDs
        queryID: queryID,
      });
    } catch (error) {
      console.warn('[Analytics] Failed to send Algolia view event:', error);
    }
  }
  
  return trackEvent({
    category: 'search',
    action: 'search_query',
    query: params.query,
    label: params.query,
    hits_count: params.hitsCount,
    locale: params.locale,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
  });
}

/**
 * Track a filter addition with enhanced Algolia analytics
 */
export function trackFilterAdd(params: {
  filterKey: string;
  filterValue: string;
  locale?: string;
}) {
  // Enhanced Algolia tracking for filter usage
  if (typeof window !== 'undefined' && window.aa) {
    const queryID = getCurrentQueryID();
    if (queryID) {
      try {
        window.aa('clickedFilters', {
          eventName: 'Filter Added',
          index: params.locale ? getIndexForLocale(params.locale) : undefined,
          filters: [`${params.filterKey}:${params.filterValue}`],
          queryID: queryID,
        });
      } catch (error) {
        console.warn('[Analytics] Failed to send Algolia filter event:', error);
      }
    }
  }
  
  return trackEvent({
    category: 'search',
    action: 'filter_add',
    filter_key: params.filterKey,
    filter_value: params.filterValue,
    label: `${params.filterKey}: ${params.filterValue}`,
    locale: params.locale,
  });
}

/**
 * Track a filter removal with enhanced Algolia analytics
 */
export function trackFilterRemove(params: {
  filterKey: string;
  filterValue: string;
  locale?: string;
}) {
  // Enhanced Algolia tracking for filter usage
  if (typeof window !== 'undefined' && window.aa) {
    const queryID = getCurrentQueryID();
    if (queryID) {
      try {
        window.aa('clickedFilters', {
          eventName: 'Filter Removed',
          index: params.locale ? getIndexForLocale(params.locale) : undefined,
          filters: [`${params.filterKey}:${params.filterValue}`],
          queryID: queryID,
        });
      } catch (error) {
        console.warn('[Analytics] Failed to send Algolia filter event:', error);
      }
    }
  }
  
  return trackEvent({
    category: 'search',
    action: 'filter_remove',
    filter_key: params.filterKey,
    filter_value: params.filterValue,
    label: `${params.filterKey}: ${params.filterValue}`,
    locale: params.locale,
  });
}

/**
 * Track sort change
 */
export function trackSortChange(params: {
  sortType: 'relevance' | 'date' | 'salary';
  locale?: string;
}) {
  return trackEvent({
    category: 'search',
    action: 'sort_change',
    sort_type: params.sortType,
    label: params.sortType,
    locale: params.locale,
  });
}

/**
 * Track job click
 */
export function trackJobClick(params: {
  jobId: string;
  jobTitle: string;
  companyId: string;
  position?: number;
  locale?: string;
  queryId?: string;
}) {
  // Get current query ID to link click to search
  const queryID = getCurrentQueryID();
  
  // Enhanced tracking for Algolia with query ID
  if (typeof window !== 'undefined' && window.aa && queryID) {
    try {
      window.aa('clickedObjectIDsAfterSearch', {
        eventName: 'Job Clicked',
        index: params.locale ? getIndexForLocale(params.locale) : undefined,
        objectIDs: [params.jobId],
        positions: params.position ? [params.position] : undefined,
        queryID: queryID,
      });
    } catch (error) {
      console.warn('[Analytics] Failed to send Algolia click event:', error);
    }
  }
  
  return trackEvent({
    category: 'job',
    action: 'job_click',
    job_id: params.jobId,
    job_title: params.jobTitle,
    company_id: params.companyId,
    label: params.jobTitle,
    value: params.position,
    locale: params.locale,
  });
}

/**
 * Track job view (detail page view)
 */
export function trackJobView(params: {
  jobId: string;
  jobTitle: string;
  companyId: string;
  locale?: string;
}) {
  return trackEvent({
    category: 'job',
    action: 'job_view',
    job_id: params.jobId,
    job_title: params.jobTitle,
    company_id: params.companyId,
    label: params.jobTitle,
    locale: params.locale,
  });
}

/**
 * Track job save
 */
export function trackJobSave(params: {
  jobId: string;
  jobTitle: string;
  locale?: string;
}) {
  return trackEvent({
    category: 'job',
    action: 'job_save',
    job_id: params.jobId,
    job_title: params.jobTitle,
    label: params.jobTitle,
    locale: params.locale,
  });
}

/**
 * Track job unsave
 */
export function trackJobUnsave(params: {
  jobId: string;
  jobTitle: string;
  locale?: string;
}) {
  return trackEvent({
    category: 'job',
    action: 'job_unsave',
    job_id: params.jobId,
    job_title: params.jobTitle,
    label: params.jobTitle,
    locale: params.locale,
  });
}

/**
 * Track job share
 */
export function trackJobShare(params: {
  jobId: string;
  jobTitle: string;
  locale?: string;
}) {
  return trackEvent({
    category: 'job',
    action: 'job_share',
    job_id: params.jobId,
    job_title: params.jobTitle,
    label: params.jobTitle,
    locale: params.locale,
  });
}

/**
 * Track company link click
 */
export function trackCompanyLink(params: {
  companyId: string;
  locale?: string;
}) {
  return trackEvent({
    category: 'interaction',
    action: 'company_link_click',
    company_id: params.companyId,
    label: params.companyId,
    locale: params.locale,
  });
}

/**
 * Track pagination
 */
export function trackPagination(params: {
  pageNumber: number;
  locale?: string;
}) {
  return trackEvent({
    category: 'navigation',
    action: 'search_pagination',
    page_number: params.pageNumber,
    label: `Page ${params.pageNumber}`,
    locale: params.locale,
  });
}

/**
 * Track search suggestion click
 */
export function trackSearchSuggestion(params: {
  suggestion: string;
  locale?: string;
}) {
  return trackEvent({
    category: 'search',
    action: 'search_suggestion_click',
    query: params.suggestion,
    label: params.suggestion,
    locale: params.locale,
  });
}

/**
 * Track job application (conversion event)
 */
export function trackJobApply(params: {
  jobId: string;
  jobTitle: string;
  companyId: string;
  position?: number;
  locale?: string;
  method?: 'external_link' | 'email' | 'company_website';
}) {
  const queryID = getCurrentQueryID();
  
  // GA4 conversion tracking
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: `${import.meta.env?.PUBLIC_ENV__GOOGLE_ANALYTICS}/job_apply`,
      value: 1,
      currency: 'EUR',
      job_id: params.jobId,
      job_title: params.jobTitle,
      company_id: params.companyId,
      method: params.method || 'external_link',
      query_id: queryID,
      locale: params.locale,
    });
    
    // Also send as custom event
    window.gtag('event', 'job_apply_conversion', {
      category: 'conversion',
      job_id: params.jobId,
      job_title: params.jobTitle,
      company_id: params.companyId,
      method: params.method,
      value: 1,
      locale: params.locale,
    });
  }
  
  // Algolia conversion event
  if (typeof window !== 'undefined' && window.aa && queryID) {
    try {
      window.aa('convertedObjectIDsAfterSearch', {
        eventName: 'Job Application',
        index: params.locale ? getIndexForLocale(params.locale) : undefined,
        objectIDs: [params.jobId],
        positions: params.position ? [params.position] : undefined,
        queryID: queryID,
      });
    } catch (error) {
      console.warn('[Analytics] Failed to send Algolia conversion event:', error);
    }
  }
  
  // Sentry breadcrumb
  if (typeof window !== 'undefined') {
    Sentry.addBreadcrumb({
      category: 'conversion',
      message: 'Job Application',
      level: 'info',
      data: {
        job_id: params.jobId,
        job_title: params.jobTitle,
        company_id: params.companyId,
        method: params.method,
        query_id: queryID,
      },
    });
  }
  
  return {
    ga4: true,
    algolia: !!queryID,
    sentry: true,
  };
}

/**
 * Track error (also sends to Sentry)
 */
export function trackError(params: {
  error: Error | string;
  context?: Record<string, any>;
  level?: 'info' | 'warning' | 'error' | 'fatal';
}) {
  const errorMessage = typeof params.error === 'string' ? params.error : params.error.message;
  
  // Send to Sentry with full context
  Sentry.captureException(params.error, {
    level: params.level || 'error',
    extra: params.context,
  });

  // Also track as analytics event
  return trackEvent({
    category: 'error',
    action: 'error_occurred',
    label: errorMessage,
    ...params.context,
  });
}

